import React, { useState, useEffect } from "react";
import api from "../../services/api";
import "./EatitGame.css";

const DEFAULT_CONFIG = {
  isGameActive: false,
  quizDailyLimit: 1,
  riddleDailyLimit: 1,
  spinDailyLimit: 3,
  maxDailyXP: 100,
  questionTimer: 15,
  xpToPoint: 1,
  pointToMoney: 1,
  minRedeemPoints: 200,
  maxRedeemPerOrder: 10,
  pointExpireDays: 30,
  xpPerDifficulty: {
    Easy: { correct: 2, wrong: -1 },
    Medium: { correct: 4, wrong: -3 },
    Hard: { correct: 8, wrong: -5 },
  },
  streakBonuses: { three: 5, five: 10, ten: 20 },
  spinProbabilities: [
    { xp: 5, probability: 30 },
    { xp: 10, probability: 25 },
    { xp: 20, probability: 20 },
    { xp: 40, probability: 15 },
    { xp: 80, probability: 7 },
    { xp: 150, probability: 2.5 },
    { xp: 200, probability: 0.5 },
  ],
};

const EMPTY_QUESTION = {
  question: "",
  options: ["", "", "", ""],
  correctIndex: 0,
  difficulty: "Easy",
  hint: "",
  gameType: "quiz",
};

export default function EatitGame() {
  const [activeTab, setActiveTab] = useState("quiz");
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [configSaving, setConfigSaving] = useState(false);
  const [configMsg, setConfigMsg] = useState("");
  const [questions, setQuestions] = useState([]);
  const [qLoading, setQLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editQuestion, setEditQuestion] = useState(null);
  const [form, setForm] = useState(EMPTY_QUESTION);
  const [formMsg, setFormMsg] = useState("");

  useEffect(() => {
    fetchConfig();
  }, []);

  useEffect(() => {
    if (activeTab !== "spin") fetchQuestions(activeTab);
  }, [activeTab]);

  const fetchConfig = async () => {
    try {
      const res = await api.get("/game/config");
      if (res.data?.config) setConfig({ ...DEFAULT_CONFIG, ...res.data.config });
    } catch (e) {}
  };

  const fetchQuestions = async (type) => {
    setQLoading(true);
    try {
      const res = await api.get(`/game/questions?gameType=${type}`);
      setQuestions(res.data?.questions || []);
    } catch (e) {}
    setQLoading(false);
  };

  const saveConfig = async () => {
    setConfigSaving(true);
    setConfigMsg("");
    try {
      await api.put("/game/config", config);
      setConfigMsg("Saved successfully!");
    } catch (e) {
      setConfigMsg("Save failed.");
    }
    setConfigSaving(false);
    setTimeout(() => setConfigMsg(""), 3000);
  };

  const openAdd = (type) => {
    setForm({ ...EMPTY_QUESTION, gameType: type });
    setEditQuestion(null);
    setFormMsg("");
    setShowAddModal(true);
  };

  const openEdit = (q) => {
    setForm({ ...q });
    setEditQuestion(q._id);
    setFormMsg("");
    setShowAddModal(true);
  };

  const deleteQ = async (id) => {
    if (!window.confirm("Delete this question?")) return;
    await api.delete(`/game/questions/${id}`);
    fetchQuestions(activeTab);
  };

  const submitForm = async () => {
    if (!form.question.trim() || form.options.some((o) => !o.trim())) {
      setFormMsg("Fill all fields.");
      return;
    }
    try {
      if (editQuestion) {
        await api.put(`/game/questions/${editQuestion}`, form);
      } else {
        await api.post("/game/questions", form);
      }
      setShowAddModal(false);
      fetchQuestions(activeTab);
    } catch (e) {
      setFormMsg("Failed to save question.");
    }
  };

  const updateOption = (i, val) => {
    const opts = [...form.options];
    opts[i] = val;
    setForm({ ...form, options: opts });
  };

  const updateSpinProb = (i, field, val) => {
    const probs = [...config.spinProbabilities];
    probs[i] = { ...probs[i], [field]: Number(val) };
    setConfig({ ...config, spinProbabilities: probs });
  };

  const difficultyColor = { Easy: "#10B981", Medium: "#F59E0B", Hard: "#EF4444" };

  return (
    <div className="eg-container">
      <div className="eg-header">
        <h2 className="eg-title">Eatit Games</h2>
        <label className="eg-toggle">
          <span>Game Active</span>
          <input
            type="checkbox"
            checked={config.isGameActive}
            onChange={async (e) => {
              const updated = { ...config, isGameActive: e.target.checked };
              setConfig(updated);
              try { await api.put("/game/config", updated); } catch (err) {}
            }}
          />
          <span className={`eg-toggle-slider ${config.isGameActive ? "on" : ""}`} />
        </label>
      </div>

      {/* Tabs */}
      <div className="eg-tabs">
        {["quiz", "riddle", "spin"].map((t) => (
          <button
            key={t}
            className={`eg-tab ${activeTab === t ? "active" : ""}`}
            onClick={() => setActiveTab(t)}
          >
            {t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      {/* Config Section */}
      <div className="eg-section">
        <h3 className="eg-section-title">Game Validations & Config</h3>
        <div className="eg-config-grid">
          {activeTab === "quiz" && (
            <>
              <ConfigField label="Quiz Daily Limit" value={config.quizDailyLimit} onChange={(v) => setConfig({ ...config, quizDailyLimit: Number(v) })} />
              <ConfigField label="Max Daily XP" value={config.maxDailyXP} onChange={(v) => setConfig({ ...config, maxDailyXP: Number(v) })} />
              <ConfigField label="Question Timer (sec)" value={config.questionTimer} onChange={(v) => setConfig({ ...config, questionTimer: Number(v) })} />
              <ConfigField label="XP to Point" value={config.xpToPoint} onChange={(v) => setConfig({ ...config, xpToPoint: Number(v) })} />
              <ConfigField label="Point to Money (₹)" value={config.pointToMoney} onChange={(v) => setConfig({ ...config, pointToMoney: Number(v) })} />
              <ConfigField label="Min Redeem Points" value={config.minRedeemPoints} onChange={(v) => setConfig({ ...config, minRedeemPoints: Number(v) })} />
              <ConfigField label="Max Redeem Per Order" value={config.maxRedeemPerOrder} onChange={(v) => setConfig({ ...config, maxRedeemPerOrder: Number(v) })} />
              <ConfigField label="Point Expire Days" value={config.pointExpireDays} onChange={(v) => setConfig({ ...config, pointExpireDays: Number(v) })} />
            </>
          )}
          {activeTab === "riddle" && (
            <>
              <ConfigField label="Riddle Daily Limit" value={config.riddleDailyLimit} onChange={(v) => setConfig({ ...config, riddleDailyLimit: Number(v) })} />
              <ConfigField label="Max Daily XP" value={config.maxDailyXP} onChange={(v) => setConfig({ ...config, maxDailyXP: Number(v) })} />
              <ConfigField label="Question Timer (sec)" value={config.questionTimer} onChange={(v) => setConfig({ ...config, questionTimer: Number(v) })} />
            </>
          )}
          {activeTab === "spin" && (
            <ConfigField label="Spin Daily Limit" value={config.spinDailyLimit} onChange={(v) => setConfig({ ...config, spinDailyLimit: Number(v) })} />
          )}
        </div>

        {/* XP Per Difficulty */}
        {activeTab !== "spin" && (
          <div className="eg-diff-section">
            <h4>XP Per Difficulty</h4>
            <div className="eg-diff-grid">
              {["Easy", "Medium", "Hard"].map((d) => (
                <div key={d} className="eg-diff-card" style={{ borderColor: difficultyColor[d] }}>
                  <span style={{ color: difficultyColor[d], fontWeight: 700 }}>{d}</span>
                  <div className="eg-diff-row">
                    <label>Correct XP</label>
                    <input
                      type="number"
                      value={config.xpPerDifficulty?.[d]?.correct ?? 0}
                      onChange={(e) => setConfig({
                        ...config,
                        xpPerDifficulty: {
                          ...config.xpPerDifficulty,
                          [d]: { ...config.xpPerDifficulty[d], correct: Number(e.target.value) },
                        },
                      })}
                    />
                  </div>
                  <div className="eg-diff-row">
                    <label>Wrong XP</label>
                    <input
                      type="number"
                      value={config.xpPerDifficulty?.[d]?.wrong ?? 0}
                      onChange={(e) => setConfig({
                        ...config,
                        xpPerDifficulty: {
                          ...config.xpPerDifficulty,
                          [d]: { ...config.xpPerDifficulty[d], wrong: Number(e.target.value) },
                        },
                      })}
                    />
                  </div>
                </div>
              ))}
            </div>
            <div className="eg-streak-section">
              <h4>Streak Bonuses</h4>
              <div className="eg-diff-grid">
                {[["3 Correct", "three"], ["5 Correct", "five"], ["10 Correct", "ten"]].map(([label, key]) => (
                  <div key={key} className="eg-diff-card">
                    <span>{label}</span>
                    <input
                      type="number"
                      value={config.streakBonuses?.[key] ?? 0}
                      onChange={(e) => setConfig({
                        ...config,
                        streakBonuses: { ...config.streakBonuses, [key]: Number(e.target.value) },
                      })}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Spin Probabilities */}
        {activeTab === "spin" && (
          <div className="eg-spin-probs">
            <h4>Spin Probabilities</h4>
            <table className="eg-table">
              <thead>
                <tr><th>XP Value</th><th>Probability (%)</th></tr>
              </thead>
              <tbody>
                {config.spinProbabilities?.map((p, i) => (
                  <tr key={i}>
                    <td><input type="number" value={p.xp} onChange={(e) => updateSpinProb(i, "xp", e.target.value)} /></td>
                    <td><input type="number" step="0.1" value={p.probability} onChange={(e) => updateSpinProb(i, "probability", e.target.value)} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="eg-prob-note">Total: {config.spinProbabilities?.reduce((s, p) => s + p.probability, 0).toFixed(1)}% (should be 100%)</p>
          </div>
        )}

        <div className="eg-save-row">
          <button className="eg-btn-primary" onClick={saveConfig} disabled={configSaving}>
            {configSaving ? "Saving..." : "Save Config"}
          </button>
          {configMsg && <span className="eg-msg">{configMsg}</span>}
        </div>
      </div>

      {/* Questions Section */}
      {activeTab !== "spin" && (
        <div className="eg-section">
          <div className="eg-section-header">
            <h3 className="eg-section-title">{activeTab === "quiz" ? "Quiz" : "Riddle"} Questions ({questions.length})</h3>
            <button className="eg-btn-primary" onClick={() => openAdd(activeTab)}>+ Add Question</button>
          </div>
          {qLoading ? (
            <p>Loading...</p>
          ) : (
            <div className="eg-questions-list">
              {questions.map((q, i) => (
                <div key={q._id} className="eg-question-card">
                  <div className="eg-q-header">
                    <span className="eg-q-num">Q{i + 1}</span>
                    <span className="eg-q-diff" style={{ background: difficultyColor[q.difficulty] + "22", color: difficultyColor[q.difficulty] }}>{q.difficulty}</span>
                    <div className="eg-q-actions">
                      <button className="eg-btn-sm" onClick={() => openEdit(q)}>Edit</button>
                      <button className="eg-btn-sm danger" onClick={() => deleteQ(q._id)}>Delete</button>
                    </div>
                  </div>
                  <p className="eg-q-text">{q.question}</p>
                  <div className="eg-q-options">
                    {q.options.map((opt, oi) => (
                      <span key={oi} className={`eg-q-opt ${oi === q.correctIndex ? "correct" : ""}`}>
                        {String.fromCharCode(65 + oi)}. {opt}
                      </span>
                    ))}
                  </div>
                  {q.hint && <p className="eg-q-hint">💡 {q.hint}</p>}
                </div>
              ))}
              {questions.length === 0 && <p className="eg-empty">No questions yet. Add some!</p>}
            </div>
          )}
        </div>
      )}

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="eg-modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="eg-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{editQuestion ? "Edit Question" : "Add Question"}</h3>
            <div className="eg-form-group">
              <label>Game Type</label>
              <select value={form.gameType} onChange={(e) => setForm({ ...form, gameType: e.target.value })}>
                <option value="quiz">Quiz</option>
                <option value="riddle">Riddle</option>
              </select>
            </div>
            <div className="eg-form-group">
              <label>Difficulty</label>
              <select value={form.difficulty} onChange={(e) => setForm({ ...form, difficulty: e.target.value })}>
                <option>Easy</option>
                <option>Medium</option>
                <option>Hard</option>
              </select>
            </div>
            <div className="eg-form-group">
              <label>Question</label>
              <textarea value={form.question} onChange={(e) => setForm({ ...form, question: e.target.value })} rows={3} />
            </div>
            {form.options.map((opt, i) => (
              <div key={i} className="eg-form-group eg-option-row">
                <label>
                  <input type="radio" name="correct" checked={form.correctIndex === i} onChange={() => setForm({ ...form, correctIndex: i })} />
                  {" "}Option {String.fromCharCode(65 + i)}
                </label>
                <input type="text" value={opt} onChange={(e) => updateOption(i, e.target.value)} />
              </div>
            ))}
            <div className="eg-form-group">
              <label>Hint (Fun Fact)</label>
              <input type="text" value={form.hint} onChange={(e) => setForm({ ...form, hint: e.target.value })} />
            </div>
            {formMsg && <p className="eg-msg error">{formMsg}</p>}
            <div className="eg-modal-actions">
              <button className="eg-btn-secondary" onClick={() => setShowAddModal(false)}>Cancel</button>
              <button className="eg-btn-primary" onClick={submitForm}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ConfigField({ label, value, onChange }) {
  return (
    <div className="eg-config-field">
      <label>{label}</label>
      <input type="number" value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}
